import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { toast } from 'react-toastify'
import Joi from 'joi'
import Swal from 'sweetalert2'
import PropTypes from 'prop-types'

import Btn from '../../../../../UI/Btn'
import SelectField from '../../../../../UI/SelectField'
import TextAreaField from '../../../../../UI/TextAreaField'
import http from '../../../../../shared/http'
import { TApi } from '../../../../../shared/const'
import { schemaOptions } from '../../../../../shared/i18n'
import { canonicalOptionValue, optionLabel, optionsFor } from '../../../../../shared/optionList'
import { defaultReleaseDay, isReleaseActive, releaseLastDay } from '../../../../../shared/revisionRelease'
import HygieneTraining from '../../../common/HygieneTraining'

import styles from '../style.module.scss'

const schema = Joi.object({
	branch: Joi.string().required(),
	offerTypes: Joi.string().required(),
	businessIdea: Joi.string().min(100).max(200).required()
})

const escapeHtml = text =>
	String(text ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')

/**
 * Die Folgen einer Änderung als Sätze — aus der Antwort des Servers
 * (`consequences`, `POST admin/company/:id/business/preview`). Ob die
 * Hygiene-Regel vorher oder nachher gilt, rechnet allein der Server.
 */
export const consequenceLines = (t, consequences) => {
	const lines = []
	if (consequences?.hygiene === 'DROPPED') lines.push(t('Business change hygiene dropped'))
	if (consequences?.hygiene === 'REQUIRED') lines.push(t('Business change hygiene required'))
	if (consequences?.hygiene === 'KEPT') lines.push(t('Business change hygiene kept'))
	if (consequences?.certificate) lines.push(t('Business change certificate renewed'))
	if (consequences?.listsFollow) lines.push(t('Business change lists follow'))
	lines.push(t('Business change activation stays'))
	return lines
}

/**
 * Reiter „Geschaeftstaetigkeit": Was das Unternehmen anbietet.
 *
 * Seit 08.08.2026 steht die **Kurzbeschreibung der Geschaeftsidee an erster
 * Stelle** — sie geht woertlich in den Firmenbuchauszug und in die
 * Praesentation im JAA-Portal und ist damit die Angabe, die am weitesten
 * hinauswirkt. Die zweite, laengere Beschreibung („Detaillierte
 * Geschaeftstaetigkeitsbeschreibung", Feld `businessActivityDescription`) ist
 * ersatzlos entfallen; sie stand neben der Beschreibung, ohne etwas
 * hinzuzufuegen. **Die Beschreibung darunter ist `description`** — bis
 * 08.08.2026 zeigte dieser Reiter unter „Beschreibung" versehentlich das
 * andere Feld an.
 *
 * **Bearbeiten durch die Verwaltung (Anforderung #230)** — auch nach der
 * Aktivierung: Branche, Angebotsart und Geschaeftsidee. Vor dem Speichern
 * fragt ein Fenster nach und nennt die Folgen, die der Server ausrechnet
 * (Hygiene-Bestaetigung, Firmenbuchauszug, Listen). Dazu der Block
 * „Bearbeitung durch die Schueler:innen": Freigabe der Geschaeftstaetigkeit
 * und der Markt- und Risikoanalyse, befristet oder bis zum Zuruecknehmen.
 * Beides nur mit `canEdit` — die Lehrkraft und Alumni lesen, und eine
 * Landesadministration aus einem anderen Bundesland bekommt keinen Knopf,
 * den der Server ablehnen wuerde.
 */
export default function BusinessTab(props) {
	const { t, i18n } = useTranslation()
	const { companyInfo, typeOfOffers, branch, canEdit, revisionRelease, onChanged } = props

	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [releasing, setReleasing] = useState(false)

	const stored = {
		branch: canonicalOptionValue(branch, companyInfo?.branch) || '',
		offerTypes: canonicalOptionValue(typeOfOffers, companyInfo?.offerTypes) || '',
		businessIdea: companyInfo?.businessIdea || ''
	}

	const {
		control,
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: { labels: false, language: localStorage.getItem('i18nextLng') },
			...schemaOptions
		}),
		defaultValues: stored
	})

	// Der gespeicherte Wert kann deutsch oder englisch sein — siehe
	// shared/optionList.
	const offerLabel = optionLabel(typeOfOffers, companyInfo?.offerTypes, i18n.language)
	// Die Branche stand hier bis 22.08.2026 gar nicht — mit der Hygiene-Regel
	// (Anforderung #96) entscheidet sie mit, was die Junior Company erfüllen muss.
	const branchLabel = optionLabel(branch, companyInfo?.branch, i18n.language)

	const startEditing = () => {
		reset(stored)
		setEditing(true)
	}

	const labelFor = (field, value) => {
		if (field === 'branch') return optionLabel(branch, value, i18n.language) || '—'
		if (field === 'offerTypes') return optionLabel(typeOfOffers, value, i18n.language) || '—'
		return value || '—'
	}

	const FIELD_LABELS = { branch: t('branch'), offerTypes: t('typeOffers'), businessIdea: t('Business Idea') }

	const onSubmit = async data => {
		// Nur, was sich geändert hat: Eine ältere Geschäftsidee unter 100 Zeichen
		// soll eine reine Branchenänderung nicht aufhalten.
		const changed = Object.fromEntries(
			Object.entries(data).filter(([field, value]) => (value || '').trim() !== (stored[field] || '').trim())
		)

		if (!Object.keys(changed).length) {
			toast.info(t('No changes to save'))
			setEditing(false)
			return
		}

		setSaving(true)
		try {
			const { data: preview } = await http.post(TApi.ADMIN_COMPANY_BUSINESS_PREVIEW(companyInfo.id), changed)

			const changeRows = Object.keys(changed)
				.map(
					field =>
						`<li><b>${escapeHtml(FIELD_LABELS[field])}:</b> ${escapeHtml(
							labelFor(field, stored[field])
						)} → ${escapeHtml(labelFor(field, changed[field]))}</li>`
				)
				.join('')
			const folgen = consequenceLines(t, preview?.consequences)
				.map(line => `<li>${escapeHtml(line)}</li>`)
				.join('')

			const { isConfirmed } = await Swal.fire({
				title: t('Change business activity?'),
				html: `<div style="text-align:left"><p>${escapeHtml(
					t('Business change what changes')
				)}</p><ul>${changeRows}</ul><p>${escapeHtml(
					t('Business change consequences')
				)}</p><ul>${folgen}</ul></div>`,
				icon: 'question',
				width: 640,
				showCancelButton: true,
				confirmButtonColor: '#008b9c',
				cancelButtonColor: '#6c757d',
				confirmButtonText: t('save'),
				cancelButtonText: t('Cancel')
			})

			if (!isConfirmed) return

			const { data: result } = await http.put(TApi.ADMIN_COMPANY_BUSINESS(companyInfo.id), changed)

			if (result?.certificate === 'FAILED') {
				toast.error(t('Business change certificate failed'))
			} else if (result?.certificate === 'RENEWED') {
				toast.success(t('Business change saved, certificate renewed'))
			} else {
				toast.success(t('Successfully updated'))
			}

			setEditing(false)
			onChanged?.()
		} catch (error) {
			// Die Meldung kommt aus shared/http.
		} finally {
			setSaving(false)
		}
	}

	const releaseCompany = async () => {
		const { isConfirmed, value } = await Swal.fire({
			title: t('Release for revision'),
			html: `<div style="text-align:left">
				<p>${escapeHtml(t('Release for revision explanation'))}</p>
				<label style="display:block;margin:14px 0 6px;font-weight:600">${escapeHtml(t('Released until (inclusive)'))}</label>
				<input id="release-until" type="date" class="swal2-input" style="margin:0;width:100%" value="${defaultReleaseDay()}" />
				<label style="display:flex;gap:8px;align-items:center;margin-top:12px">
					<input id="release-open" type="checkbox" /> ${escapeHtml(t('Until revoked'))}
				</label>
			</div>`,
			width: 600,
			showCancelButton: true,
			confirmButtonColor: '#008b9c',
			cancelButtonColor: '#6c757d',
			confirmButtonText: t('Release'),
			cancelButtonText: t('Cancel'),
			preConfirm: () => {
				const openEnded = !!document.getElementById('release-open')?.checked
				const until = document.getElementById('release-until')?.value || ''
				if (!openEnded && !until) {
					Swal.showValidationMessage(t('Please choose an end date'))
					return false
				}
				return openEnded ? { openEnded: true } : { until }
			}
		})

		if (!isConfirmed) return

		setReleasing(true)
		http.put(TApi.ADMIN_COMPANY_REVISION_RELEASE(companyInfo.id), value)
			.then(() => {
				toast.success(t('Released for revision'))
				onChanged?.()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setReleasing(false))
	}

	const revokeRelease = async () => {
		const { isConfirmed } = await Swal.fire({
			title: t('Revoke release?'),
			text: t('Revoke release explanation'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#6c757d',
			confirmButtonText: t('Revoke release'),
			cancelButtonText: t('Cancel')
		})

		if (!isConfirmed) return

		setReleasing(true)
		http.delete(TApi.ADMIN_COMPANY_REVISION_RELEASE(companyInfo.id))
			.then(() => {
				toast.success(t('Release revoked'))
				onChanged?.()
			})
			.catch(() => {})
			.finally(() => setReleasing(false))
	}

	const releaseActive = isReleaseActive(revisionRelease)
	const releaseStatus = releaseActive
		? revisionRelease.openEnded
			? t('Released for revision until further notice')
			: t('Released for revision until {{date}}', { date: releaseLastDay(revisionRelease.until) })
		: revisionRelease?.expired
		? t('Release expired on {{date}}', { date: releaseLastDay(revisionRelease.until) })
		: t('Locked for students since the activation')

	return (
		<div className={styles.stack}>
			{canEdit && (
				<div className={styles.blockActions}>
					{!editing ? (
						<Btn
							className={styles.btnVerify}
							iconClassName={styles.btnIconReset}
							type="button"
							icon="edit"
							iconOrder={0}
							iconSize={{ w: 19, h: 19 }}
							iconColor="#008b9c"
							onClick={startEditing}
						>
							{t('Edit')}
						</Btn>
					) : (
						<>
							<Btn
								type="button"
								loading={saving}
								icon="save"
								iconSize={{ w: 15, h: 'auto' }}
								iconColor="#fff"
								onClick={handleSubmit(onSubmit)}
							>
								{t('save')}
							</Btn>
							<Btn type="button" theme="grey" disabled={saving} onClick={() => setEditing(false)}>
								{t('Cancel')}
							</Btn>
						</>
					)}
				</div>
			)}

			<section className={styles.block}>
				<div className={styles.blockHead}>
					<h3 className={styles.blockTitle}>{t('Business Idea')}</h3>
					<span className={styles.blockHint}>{t('Character limit', { max: 200 })}</span>
				</div>
				<p className={styles.blockNote}>{t('Business idea purpose')}</p>
				{editing ? (
					<TextAreaField
						classNameLabel={styles.logTextArea}
						height={120}
						label={t('Your Business Idea')}
						placeholder={t('We are the...')}
						error={errors.businessIdea}
						showLettersLeft
						maxLength={200}
						value={watch('businessIdea')}
						register={register('businessIdea')}
						disabled={saving}
					/>
				) : (
					<div className={styles.readOnlyTextArea}>{companyInfo?.businessIdea || ''}</div>
				)}
			</section>

			<section className={styles.block}>
				<h3 className={styles.blockTitle}>{t('Business Activity')}</h3>
				{editing ? (
					<div className={styles.fieldGrid}>
						<SelectField
							label={t('branch')}
							control={control}
							name="branch"
							error={errors.branch}
							placeholder={t('Select Branch')}
							options={optionsFor(branch, i18n.language, stored.branch)}
							disabled={saving}
						/>
						<SelectField
							label={t('typeOffers')}
							control={control}
							name="offerTypes"
							error={errors.offerTypes}
							placeholder={t('typeOffers')}
							options={optionsFor(typeOfOffers, i18n.language, stored.offerTypes)}
							disabled={saving}
						/>
					</div>
				) : (
					<div className={styles.fieldGrid}>
						<div className={styles.readOnlyInput}>
							<h3 className={styles.readOnlyLabel}>{t('branch')}</h3>
							<div className={styles.readOnlyText}>
								{branchLabel || <span className={styles.emptyValue}>—</span>}
							</div>
						</div>
						<div className={styles.readOnlyInput}>
							<h3 className={styles.readOnlyLabel}>{t('typeOffers')}</h3>
							<div className={styles.readOnlyText}>
								{offerLabel || <span className={styles.emptyValue}>—</span>}
							</div>
						</div>
					</div>
				)}

				{/*
				 * Lebensmittelhygiene und Allergeninformation (Anforderung #96):
				 * nur zu lesen. Die Verwaltung entscheidet über die Aktivierung und
				 * soll dabei sehen, wer wann was bestätigt hat — eingreifen tut sie
				 * hier nicht.
				 */}
				<HygieneTraining readOnly companyId={companyInfo?.id} savedBranch={companyInfo?.branch} />
				<div className={styles.readOnlyInput}>
					<h3 className={styles.readOnlyLabel}>{t('description')}</h3>
					<div className={styles.readOnlyTextArea}>{companyInfo?.description || ''}</div>
				</div>
			</section>

			{/*
			 * Freigabe zur Überarbeitung (#230). Nur bei einer aktiven Company —
			 * vorher bearbeiten die Schüler:innen ohnehin selbst.
			 */}
			{!!companyInfo?.isActive && (
				<section className={styles.block} data-testid="revision-release">
					<h3 className={styles.blockTitle}>{t('Editing by the students')}</h3>
					<p className={styles.blockNote}>{t('Editing by the students note')}</p>
					<div className={styles.readOnlyText}>{releaseStatus}</div>
					{canEdit && (
						<div className={styles.blockActions}>
							<Btn
								type="button"
								className={styles.btnVerify}
								iconClassName={styles.btnIconReset}
								loading={releasing}
								onClick={releaseCompany}
							>
								{releaseActive ? t('Change release period') : t('Release editing for students')}
							</Btn>
							{releaseActive && (
								<Btn
									type="button"
									className={styles.btnVerify}
									iconClassName={styles.btnIconReset}
									disabled={releasing}
									onClick={revokeRelease}
								>
									{t('Revoke release')}
								</Btn>
							)}
						</div>
					)}
				</section>
			)}
		</div>
	)
}

BusinessTab.propTypes = {
	companyInfo: PropTypes.object,
	typeOfOffers: PropTypes.array,
	branch: PropTypes.array,
	// Bearbeiten und Freigeben: nur Landes-/Bundesadmin mit Zugriff auf diese
	// Company. Ohne (Lehrkraft, Alumni, fremdes Bundesland) nur lesen.
	canEdit: PropTypes.bool,
	revisionRelease: PropTypes.object,
	onChanged: PropTypes.func
}
